import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, MapPin, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const INTERIOR = "https://images.pexels.com/photos/33685861/pexels-photo-33685861.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function CityLanding({ city, slug }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 4))).catch(() => {});
  }, [slug]);

  return (
    <div data-testid={`city-${slug}`}>
      <Seo title={`Surface Protection in ${city}`}
        description={`ERRAH surface protection in ${city} — marble, granite, quartz, glass, wood and furniture protection. Book an AI surface scan and installation in ${city}.`} />
      <section className="relative min-h-[52vh] flex items-center overflow-hidden">
        <img src={INTERIOR} alt={`${city} interiors`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          <p className="errah-label gold-text mb-4 flex items-center gap-2"><MapPin size={14} /> {city}</p>
          <h1 className="font-display text-5xl lg:text-7xl font-light tracking-tighter leading-none">Surface protection<br />in {city}.</h1>
          <p className="mt-6 text-gray-300 max-w-xl">Premium film, coating and DIY protection for homes, hospitality and commercial spaces across {city}.</p>
          <div className="mt-8 flex gap-3">
            <Link to="/ai-scan" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors"><ScanLine size={16} /> Scan your surface</Link>
            <Link to="/quote" className="inline-flex items-center gap-2 border border-white/20 px-7 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">Book in {city}</Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {[`Serving homes & businesses across ${city}`, "Marble, granite, quartz, glass, wood & furniture", "Professional installation + digital warranty"].map((c) => (
            <div key={c} className="flex gap-3 text-sm text-gray-400 hair-border bg-white/[0.02] p-6"><Check size={18} className="text-[#C9A45C] shrink-0" />{c}</div>
          ))}
        </div>

        {products.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display text-3xl mb-6">Popular in {city}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {products.map((p) => (
                <Reveal key={p.id}>
                  <Link to={`/products/${p.id}`} className="group hair-border bg-white/[0.02] hover:bg-white/[0.04] transition-colors block">
                    <div className="aspect-square overflow-hidden"><img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                    <div className="p-4"><h3 className="font-display text-sm">{p.name}</h3>{p.price_from && <p className="text-xs gold-text mt-1">From ₹{p.price_from}</p>}</div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <Link to="/products" className="mt-8 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-[#C9A45C] transition-colors">View all products <ArrowRight size={14} /></Link>
          </div>
        )}
      </section>
    </div>
  );
}
